/**
 * EntrancePage — Home tab with Browse / Yours inner tabs.
 * The default "/" route for all visitors.
 */

import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuthStore } from '../../stores/authStore'
import { useShopStore } from '../../stores/shopStore'
import { useInstallPrompt } from '../../hooks/useInstallPrompt'
import { apiUrl } from '../../config'
import LanguageSwitcher from '../LanguageSwitcher/LanguageSwitcher'
import PublicShopBrowser from '../Shops/PublicShopBrowser'
import './EntrancePage.css'

type HomeTab = 'browse' | 'yours'

export default function EntrancePage() {
  const { t } = useTranslation('entrance')
  const navigate = useNavigate()
  const user = useAuthStore((s) => s.user)
  const token = useAuthStore((s) => s.token)
  const setAuth = useAuthStore((s) => s.setAuth)
  const [restoring, setRestoring] = useState(true)
  const [homeTab, setHomeTab] = useState<HomeTab>('browse')
  const [homeSearch, setHomeSearch] = useState('')
  const { showBanner, canPrompt, showIosHint, promptInstall, dismiss } = useInstallPrompt()

  // Restore session from localStorage (same logic as AuthGate)
  useEffect(() => {
    if (token || user) {
      setRestoring(false)
      return
    }
    const stored = localStorage.getItem('dukkan_token')
    if (!stored) {
      setRestoring(false)
      return
    }
    fetch(apiUrl('/api/auth/me'), { headers: { Authorization: `Bearer ${stored}` } })
      .then((res) => {
        if (res.ok) return res.json()
        throw new Error('expired')
      })
      .then((u) => {
        setAuth(u, stored)
        setRestoring(false)
      })
      .catch(() => {
        localStorage.removeItem('dukkan_token')
        setRestoring(false)
      })
  }, [])

  const { shops, fetchShops: fetchUserShops, loading: shopsLoading } = useShopStore()

  // Fetch user's shops when switching to Yours tab
  useEffect(() => {
    if (homeTab === 'yours' && user && shops.length === 0) {
      fetchUserShops()
    }
  }, [homeTab, user])

  const handleYoursTabClick = () => {
    if (!user) {
      localStorage.setItem('dukkan_redirect_after_login', '/')
      navigate('/login')
      return
    }
    setHomeTab('yours')
  }

  return (
    <div className="entrance-page">
      {/* Top bar */}
      <header className="entrance-header">
        <h1 className="entrance-logo">{t('common:dukkan')}</h1>
        <div className="entrance-header-actions">
          <LanguageSwitcher />
          {user ? (
            <button className="entrance-avatar-btn" onClick={() => navigate('/profile')}>
              {user.picture ? (
                <img src={user.picture} alt="" className="entrance-avatar" referrerPolicy="no-referrer" />
              ) : (
                <span className="entrance-avatar-fallback">{user.name?.charAt(0) || '?'}</span>
              )}
            </button>
          ) : (
            <button className="entrance-sign-in" onClick={() => navigate('/login')}>
              {t('common:signIn')}
            </button>
          )}
        </div>
      </header>

      {/* PWA Install Banner */}
      {showBanner && (
        <div className="entrance-install-banner">
          <div className="entrance-install-body">
            <span className="entrance-install-icon">📲</span>
            <div className="entrance-install-text">
              <strong>{t('installDukkanty')}</strong>
              {canPrompt && <p>{t('addToHomeScreen')}</p>}
              {showIosHint && (
                <p>
                  {t('iosTapThen')} <span className="entrance-ios-share">⬆</span> {t('iosAddToHomeScreen')}
                </p>
              )}
            </div>
          </div>
          <div className="entrance-install-actions">
            {canPrompt && (
              <button className="entrance-install-btn" onClick={promptInstall}>{t('install')}</button>
            )}
            <button className="entrance-install-dismiss" onClick={dismiss}>{t('notNow')}</button>
          </div>
        </div>
      )}

      {/* Search bar — always visible at top */}
      <div className="home-search-bar">
        <svg className="home-search-icon" width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
          <path d="M15.5 14h-.79l-.28-.27A6.471 6.471 0 0 0 16 9.5 6.5 6.5 0 1 0 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/>
        </svg>
        <input
          type="text"
          className="home-search-input"
          placeholder="Search shops..."
          value={homeSearch}
          onChange={(e) => { setHomeSearch(e.target.value); setHomeTab('browse') }}
          onFocus={() => setHomeTab('browse')}
        />
        {homeSearch && (
          <button className="home-search-clear" onClick={() => setHomeSearch('')}>×</button>
        )}
      </div>

      {/* Browse / Yours inner tab switcher */}
      <div className="home-inner-tabs">
        <button
          className={`home-inner-tab${homeTab === 'browse' ? ' home-inner-tab--active' : ''}`}
          onClick={() => setHomeTab('browse')}
        >
          Browse
        </button>
        <button
          className={`home-inner-tab${homeTab === 'yours' ? ' home-inner-tab--active' : ''}`}
          onClick={handleYoursTabClick}
        >
          Yours
        </button>
      </div>

      {/* Browse tab content — full PublicShopBrowser embedded */}
      {homeTab === 'browse' && (
        <PublicShopBrowser embedded initialSearch={homeSearch} />
      )}

      {/* Yours tab content */}
      {homeTab === 'yours' && (
        <div className="entrance-yours">
          {shopsLoading ? (
            <div className="entrance-yours-loading">Loading your shops…</div>
          ) : shops.length === 0 ? (
            <div className="entrance-yours-empty">
              <p>You haven't created any shops yet.</p>
              <button className="entrance-yours-create-btn" onClick={() => navigate('/scan')}>
                + Create Shop
              </button>
            </div>
          ) : (
            <div className="entrance-yours-list">
              {shops.map((shop) => (
                <div
                  key={shop.id}
                  className="entrance-yours-card"
                  onClick={() => navigate(`/shops/${shop.id}`)}
                >
                  {shop.thumbnail_url ? (
                    <img src={shop.thumbnail_url} alt={shop.name} className="entrance-yours-thumb" />
                  ) : (
                    <div className="entrance-yours-thumb entrance-yours-thumb--placeholder">🏪</div>
                  )}
                  <div className="entrance-yours-info">
                    <span className="entrance-yours-name">{shop.name}</span>
                    {shop.category && <span className="entrance-yours-category">{shop.category}</span>}
                  </div>
                  <button
                    className="entrance-yours-edit"
                    onClick={(e) => { e.stopPropagation(); navigate(`/shops/${shop.id}`) }}
                  >
                    Edit
                  </button>
                </div>
              ))}
              <button className="entrance-yours-create-btn" onClick={() => navigate('/scan')}>
                + New Shop
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
